import moment from 'moment';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import React, { Component } from 'react';

import { BACKEND_DATE_FORMAT } from '../../../config/helpers';

import DateRangePicker from '../Atoms/DateRangePicker';
import DropdownMultiple from '../Atoms/DropdownMultiple';
import Range from '../Atoms/Range';
import { currencyCodeAndSymbolPropTypes } from '../../../util/currency';

const optionsPropTypes = PropTypes.arrayOf(
  PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  })
);

const propTypes = exact({
  currentCurrency: currencyCodeAndSymbolPropTypes,
  filterOptions: PropTypes.shape({
    accommodationTypeIds: PropTypes.arrayOf(PropTypes.string),
    durations: PropTypes.arrayOf(PropTypes.string),
    fromDate: PropTypes.string,
    toDate: PropTypes.string,
    matches: PropTypes.arrayOf(PropTypes.string),
    packageTypes: PropTypes.string,
    page: PropTypes.number,
    priceFrom: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    priceTo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    resultsPerPage: PropTypes.number,
    siteId: PropTypes.number,
    tournaments: PropTypes.arrayOf(PropTypes.string),
    destinations: PropTypes.arrayOf(PropTypes.string),
    minPrice: PropTypes.string.isRequired,
    maxPrice: PropTypes.string.isRequired,
  }),
  filters: PropTypes.shape({
    allOptionText: PropTypes.string,
    accommodationTypes: optionsPropTypes.isRequired,
    durations: optionsPropTypes.isRequired,
    packageTypes: optionsPropTypes.isRequired,
    prices: PropTypes.arrayOf(
      PropTypes.shape({
        currency: PropTypes.string.isRequired,
        maximumPriceValue: PropTypes.number.isRequired,
        minimumPriceValue: PropTypes.number.isRequired,
      })
    ).isRequired,
    tournaments: PropTypes.arrayOf(
      PropTypes.shape({
        destinations: optionsPropTypes.isRequired,
        id: PropTypes.string.isRequired,
        matches: optionsPropTypes.isRequired,
        title: PropTypes.string.isRequired,
      })
    ).isRequired,
  }).isRequired,
  labels: PropTypes.shape({
    accommodationTypeLabelText: PropTypes.string.isRequired,
    dateLabelText: PropTypes.string.isRequired,
    destinationLabelText: PropTypes.string.isRequired,
    durationLabelText: PropTypes.string.isRequired,
    matchLabelText: PropTypes.string.isRequired,
    priceLabelText: PropTypes.string.isRequired,
  }).isRequired,
  handleReset: PropTypes.func.isRequired,
  handleValueChange: PropTypes.func.isRequired,
  resetButtonText: PropTypes.string,
  tournamentID: PropTypes.string,
});

const defaultProps = {
  filterOptions: {
    allOptionText: 'All',
  },
  resetButtonText: 'Reset',
  tournamentID: null,
  currentCurrency: {
    code: 'GBP',
    symbol: '£',
  },
};

class Filter extends Component {
  constructor(props) {
    super(props);

    this.handleDateChange = this.handleDateChange.bind(this);
    this.handleMultipleSelectChange = this.handleMultipleSelectChange.bind(this);
    this.handleRangeChange = this.handleRangeChange.bind(this);
    this.handleResetClick = this.handleResetClick.bind(this);
  }

  handleDateChange(dates) {
    const [fromDate, toDate] = dates;
    this.props.handleValueChange({
      fromDate: moment(fromDate).format(BACKEND_DATE_FORMAT),
      toDate: toDate ? moment(toDate).format(BACKEND_DATE_FORMAT) : '',
    });
  }

  handleMultipleSelectChange(fieldName, values) {
    const payload = { [fieldName]: values };
    this.props.handleValueChange(payload);
  }

  handleRangeChange(data) {
    const payload = {
      priceFrom: data.values[0],
      priceTo: data.values[1],
    };

    this.props.handleValueChange(payload);
  }

  handleResetClick() {
    this.props.handleReset();
  }

  render() {
    const mapOptions = (option) => ({
      text: option.title,
      value: option.id,
    });

    const { labels, currentCurrency, filterOptions, filters, tournamentID, resetButtonText } =
      this.props;

    const durationOptions = filters.durations.map(mapOptions);
    const accommodationTypeOptions = filters.accommodationTypes.map(mapOptions);

    let tournament = null;

    if (filters.tournaments.length === 1) {
      [tournament] = filters.tournaments;
    } else if (tournamentID) {
      [tournament] = filters.tournaments.filter((x) => x.id === tournamentID);
    }

    let matchOptions = [];
    let destinationOptions = [];

    if (tournament) {
      matchOptions = tournament.matches.map(mapOptions);
      destinationOptions = tournament.destinations.map(mapOptions);
    }

    return (
      <form className="PackageFilter PackageFilter_subSection">
        <div className="PackageFilter_fieldset">
          <div className="PackageFilter_field">
            <DateRangePicker
              blockName="PackageFilter_input"
              labelText={labels.dateLabelText}
              handleChange={this.handleDateChange}
              selectedValues={[
                filterOptions.fromDate
                  ? moment(filterOptions.fromDate).toDate()
                  : moment().toDate(),
                filterOptions.toDate ? moment(filterOptions.toDate).toDate() : null,
              ]}
            />
          </div>
          <div className="PackageFilter_field">
            <DropdownMultiple
              allOptionText={filters.allOptionText}
              blockName="PackageFilter_input"
              fieldName="durations"
              handleChange={this.handleMultipleSelectChange}
              labelText={labels.durationLabelText}
              options={durationOptions}
              selectedValues={filterOptions.durations}
            />
          </div>
          {destinationOptions.length > 0 && (
            <div className="PackageFilter_field">
              <DropdownMultiple
                allOptionText={filters.allOptionText}
                blockName="PackageFilter_input"
                fieldName="destinations"
                handleChange={this.handleMultipleSelectChange}
                labelText={labels.destinationLabelText}
                options={destinationOptions}
                selectedValues={filterOptions.destinations}
              />
            </div>
          )}
          {matchOptions.length > 0 && (
            <div className="PackageFilter_field">
              <DropdownMultiple
                allOptionText={filters.allOptionText}
                blockName="PackageFilter_input"
                fieldName="matches"
                handleChange={this.handleMultipleSelectChange}
                labelText={labels.matchLabelText}
                options={matchOptions}
                selectedValues={filterOptions.matches}
              />
            </div>
          )}
          {accommodationTypeOptions.length > 0 && (
            <div className="PackageFilter_field">
              <DropdownMultiple
                allOptionText={filters.allOptionText}
                blockName="PackageFilter_input"
                fieldName="accommodationTypeIds"
                handleChange={this.handleMultipleSelectChange}
                labelText={labels.accommodationTypeLabelText}
                options={accommodationTypeOptions}
                selectedValues={filterOptions.accommodationTypeIds}
              />
            </div>
          )}
          <div className="PackageFilter_field">
            <Range
              blockName="PackageFilter_input"
              handleChange={this.handleRangeChange}
              labelText={labels.priceLabelText}
              labelValuePrefix={currentCurrency.symbol}
              maximumValue={filterOptions.maxPrice}
              minimumValue={filterOptions.minPrice}
              selectedValues={[filterOptions.priceFrom, filterOptions.priceTo]}
              isCurrency
            />
          </div>
        </div>
        <div className="PackageFilter_reset">
          <button
            type="button"
            className="PackageFilter_resetButton"
            onClick={this.handleResetClick}
          >
            {resetButtonText}
          </button>
        </div>
      </form>
    );
  }
}

Filter.propTypes = propTypes;
Filter.defaultProps = defaultProps;

export default Filter;
